/**
 * Automation list configuration.
 *
 * Automations render through the same `CRMObjectListPage` as contacts,
 * companies and deals — so they get search, filtering, saved views and the
 * standard header for free, rather than a bespoke layout that drifts from
 * every other list in the product.
 *
 * Built by a function rather than exported as a constant because one of its
 * filters depends on runtime state: which automations are hidden depends on
 * the `internal_automations` flag.
 */

import {
  INTERNAL_SCRIPT_NAMES,
  WEBSITE_SCRIPT_NAMES,
} from '../../automations/templates';
import type { ListPageConfig } from '../../core/types';

export interface AutomationListConfigOptions {
  /**
   * Whether the `internal_automations` flag is active. Internal automations
   * are managed centrally and are not user-toggleable, so they stay hidden
   * unless a staff member has explicitly opted in.
   */
  showInternal: boolean;
}

export function buildAutomationListConfig({
  showInternal,
}: AutomationListConfigOptions): ListPageConfig {
  // Website-managed scripts belong to the Websites settings section and are
  // never shown here. Internal ones join them unless the flag is on. This
  // reproduces the filtering the settings list did in JS — but as a FORCED
  // filter group rather than a removable chip, because a user clearing it
  // would expose plumbing they cannot act on. Scheduled automations carry
  // none of the hidden script names, so nothing is filtered for them.
  const hiddenScriptNames = [
    ...WEBSITE_SCRIPT_NAMES,
    ...(showInternal ? [] : INTERNAL_SCRIPT_NAMES),
  ];

  return {
    entityType: 'ahoy_event_automation',
    entityKind: 'automation',
    title: 'Automation',
    pluralTitle: 'Automations',

    columns: [
      { kind: 'object_name', sortable: true, width: 280 },
      { kind: 'type', label: 'Type', sortable: false },
      { kind: 'property', name: 'ahoy_summary' },
      { kind: 'property', name: 'ahoy_enabled', sortable: true },
      { kind: 'property', name: 'ahoy_updated_at', sortable: true },
    ],

    // An automation is not created by filling in properties — it comes from a
    // template or from describing it to the assistant. The page supplies its
    // own create handler.
    // Readable to the user, but engine-owned or structural rather than facts
    // about the automation: the settings JSON and its schema, the script's
    // runtime state and its schema, the trigger-matching flag, the script
    // itself (it has a Code tab), and the automation→automation dependency
    // edges. None of these read as a column.
    hiddenColumnOptions: [
      'ahoy_config',
      'ahoy_config_schema',
      'ahoy_state',
      'ahoy_state_schema',
      'ahoy_match_all_entity_types',
      'ahoy_script',
      'ahoy_automation_depends_on_automation',
      'ahoy_automation_dependent_of_automation',
    ],
    createProperties: [],
    createAssociations: [],
    metrics: [],

    defaultFilters: {
      page: 1,
      page_size: 20,
      sort: 'ahoy_updated_at',
      order: 'desc',
      filter_groups: [
        {
          filters: [
            {
              property: 'ahoy_automation_name',
              operator: 'notin',
              value: hiddenScriptNames,
            },
          ],
        },
      ],
    },

    showTimeFilter: false,

    emptyState: {
      heading: 'No automations yet',
      description:
        'Automations run work for you – some when something happens in your CRM, others on a schedule you set.',
    },
  };
}
